import { memo } from 'react';
import { FormControl, FormLabel, FormControlLabel, FormGroup, Checkbox, RadioGroup, Radio, Typography, Switch, Box, Button } from '@mui/material';
import _map from 'lodash/map';
// Local files
import { Root, Container, TextContainer } from './General.styled';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import useConversations from 'hooks/useConversations';
import useMessages from 'hooks/useMessages';
import useSuccess from 'hooks/useSuccess';
import useApp from 'hooks/useApp';
import { DELETE_PATIENT } from 'helpers/confirmations';
import { patientsSelectors } from 'features/patients';
import { conversationsSelectors } from 'features/conversations';

const General = () => {
  const { getConversation, updateConversation, setDevMode, clearLocalActiveConversation } = useConversations();
  const { getMessages, clearLocalMessages } = useMessages();
  const { setSuccess } = useSuccess();
  const { openDeleteDialog } = useApp();
  const { id } = useCustomSelector(patientsSelectors.getPatientData);
  const conversations = useCustomSelector(conversationsSelectors.getConversations);
  const oldConversation = useCustomSelector(conversationsSelectors.getConversation);
  const devMode = useCustomSelector(conversationsSelectors.getDevMode);
  const processing = useCustomSelector(conversationsSelectors.isFetchingConversation);

  const fetchMessages = ({ conversation_id, mode, devMode }) => {
    clearLocalMessages();
    getMessages({ 
      conversation_id, 
      senders: devMode ? 
              ['patient', 'manager', 'bot', 'system'] : 
              mode === 'manual' ? ['patient', 'manager'] : ['patient', 'manager', 'bot'] 
    });   
  };
  const handleChange = (e, type) => {
    const conversation = { id: oldConversation.id };
    conversation[type] = type === 'orchestrate' ? e.target.checked : e.target.value;

    updateConversation(conversation)
    .then(({ error }) => {
      if (!error) {
        fetchMessages({ conversation_id: conversation.id, mode: conversation?.mode, devMode })
        setSuccess('Conversation successfully updated')
      }
    })
  };
  const handleChangeDevMode = () => {
    setDevMode();
    fetchMessages({ conversation_id: oldConversation.id, mode: oldConversation.mode, devMode: !devMode })
  }
  const handleChangeChannel = (conversation_id) => {
    clearLocalActiveConversation();
    getConversation(conversation_id)
    .then(({ payload, error }) => {
      if (!error) {
        const { conversation: {mode } } = payload;
        fetchMessages({ conversation_id, mode, devMode })
      }
    })
  };
  const handleDelete = () => openDeleteDialog({ id, type: DELETE_PATIENT });

  if (!oldConversation) return <div/>;
  return (
    <Root>
      <Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, gap: 8 }}>
          <FormControl>
            <FormLabel>Mode</FormLabel>
            <Container>
              <RadioGroup
                value={oldConversation?.mode}
                onChange={(e) => handleChange(e, 'mode')}
              >
                <FormControlLabel disabled={processing} value='auto' control={<Radio />} label='Auto' />
                <FormControlLabel disabled={processing} value='approve' control={<Radio />} label='Approve' />
                <FormControlLabel disabled={processing} value='gate' control={<Radio />} label='User Gate' />
                <FormControlLabel disabled={processing} value='manual' control={<Radio />} label='Manual' />
                <FormControlLabel
                  control={<Switch color='primary' />}
                  label='Dev mode'
                  labelPlacement='end'
                  sx={{ mb: '2px' }}
                  checked={devMode} 
                  onChange={handleChangeDevMode}
                />
              </RadioGroup>
            </Container>
          </FormControl>
          <FormControl>
            <FormLabel>Trigger</FormLabel>
            <Container>
              <RadioGroup
                value={oldConversation?.trigger}
                onChange={(e) => handleChange(e, 'trigger')}
              >
                <FormControlLabel disabled={processing} value='new_patient' control={<Radio />} label='New Patient' />
                <FormControlLabel disabled={processing} value='returning_patient' control={<Radio />} label='Returning Patient' />
                <FormControlLabel disabled={processing} value='rescheduling' control={<Radio />} label='Rescheduling' />
                <FormControlLabel disabled={processing} value='general_inquiry' control={<Radio />} label='General Inquiry' />
                <FormControlLabel disabled={processing} value='referral_source' control={<Radio />} label='Referral Source' />
                <FormControlLabel disabled={processing} value='appointment_confirmation' control={<Radio />} label='Appointment Confirmation' />
                <FormControlLabel disabled={processing} value='patient_info_gathering' control={<Radio />} label='Patient info gathering' />
              </RadioGroup>
            </Container>
          </FormControl>
        </Box>
        <FormControl sx={{ mt: 2, mb: 3 }}>
            <FormLabel>Orchestration</FormLabel>
            <FormGroup>
              <FormControlLabel
                control={<Checkbox checked={oldConversation?.orchestrate} onChange={(e) => handleChange(e, 'orchestrate')} />}
                label='Orchestration mode "ON"'
                disabled={processing}
              />
            </FormGroup>
          </FormControl>
        <FormControl sx={{ mt: 2, mb: 3 }}>
          <FormLabel>Chat Filter </FormLabel>
          <FormGroup>
            {_map(conversations, (c) => 
              <FormControlLabel
                key={c.id}
                control={<Checkbox checked={oldConversation.id === c.id} onChange={() => handleChangeChannel(c.id)} /> }
                label={c.channel.toUpperCase()}
                disabled={processing}
              />
            )}
          </FormGroup>
        </FormControl>
        <TextContainer>
          <Typography variant='caption' sx={{ fontSize: '10px' }}>
            Auto: AI generated replies are sent automaticallly <br/>
            Approve: approval is required for each generated reply <br/>
            User Gate: approval is required for each user message <br/>
            Manual: fully manual chat mode <br/> (AI is still generating replies, to view - enable dev mode)
          </Typography>
        </TextContainer>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button color='error' disabled={processing} onClick={handleDelete}>Delete patient</Button>
      </Box>
    </Root>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <General {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);